import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useIsRTL} from '@aboutyou/i18n';
import {
  AddToBasketSection,
  AddToBasketSectionSkeleton,
  AddToWishlistRedirect,
  BASPlacement,
  BreadcrumbsADP,
  BreadcrumbsADPLoading,
  CleanActiveCoupon,
  CoinsBanner,
  CouponHint,
  DESKTOP_STICKY_HEIGHT,
  GoBack,
  HotProductSection,
  InteractiveProductTile,
  LastSeenProductIdsUpdater,
  LegalReport,
  MerchantBanner,
  MerchantInfoWithLegal,
  PageMetaData,
  PageMetaDataJsonLd,
  PriceAndBrand,
  PriceAndBrandLoading,
  ProductDetailsAccordions,
  ProductDetailsAccordionsLoading,
  ProductImageDetailsModal,
  ProductImageGrid,
  ProductImageGridView,
  ProductInformationBox,
  ProductMiniAdpWithBasketFlyout,
  ProductSelection,
  ProductSelectionGridSkeleton,
  ProductUSPs,
  ProductUSPsLoading,
  ProminentBannerSection,
  PromotionBanner,
  RecoHeaderWrapperLinaria,
  Recommendations,
  SelectedSize,
  SetSelectedSize,
  SizeSelectionOpenedStateKey,
  SoldOutSimilarProductsReco,
  SoldOutSizeReminderForm,
  SoldOutState,
  useBasketFlyoutOpenedState,
  useIsCouponsEnabled,
  useIsNewBannerFrameworkActive,
  useMiniAdpDialogOpenedState,
  useProductIdFromSlug,
  useProductImageDetailsModalState,
  useProductTileEntities,
  useSelectedSizeState,
} from '@aboutyou/logic-components';
import {
  COLORS,
  Drawer,
  DropDownProps,
  Dropdown,
  Dropdown as DropdownBase,
  DropdownBody,
  DropdownBodyAlignment,
  GenericIconName,
  Icon,
  ImageType,
  ModalDialog,
  SplitViewGrid,
  Z_INDEXES,
  gridCols,
  tabletAndDownLinaria,
  useSizeRecommendationInfo,
} from '@aboutyou/ui-components';
import {AddToBasketTrackingData} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {
  StrictMode,
  Suspense,
  memo,
  useCallback,
  useRef,
  useState,
} from 'react';
import {Sticky} from 'react-stickup-ay';
import {OutfitDetailPage} from '../../../../bridge/pages/OutfitDetailPage';

const MemoPageMetaData = memo(PageMetaData);
const MemoPageMetaDataSectionJsonLd = memo(PageMetaDataJsonLd);
const MemoProminentBannerSection = memo(ProminentBannerSection);
const MemoPriceAndBrand = memo(PriceAndBrand);
const MemoAddToBasketSection = memo(AddToBasketSection);
const MemoRecommendations = memo(Recommendations);
const MemoBreadcrumbs = memo(BreadcrumbsADP);
const MemoMerchantBanner = memo(MerchantBanner);
const MemoProductSelection = memo(ProductSelection);
const MemoProductUSPs = memo(ProductUSPs);
const MemoProductImageDetailsModal = memo(ProductImageDetailsModal);
const MemoProductImageGrid = memo(ProductImageGrid);
const MemoProductInformationBox = memo(ProductInformationBox);
const MemoProductDetailsAccordions = memo(ProductDetailsAccordions);
const MemoHotProductSection = memo(HotProductSection);
const MemoSoldOutSizeReminderForm = memo(SoldOutSizeReminderForm);

export const ArticleDetail: React.FC = () => {
  const {selectedSize, setSelectedSize} = useSelectedSizeState();

  const {
    showFromProductTileEntities,
    entitiesBrandLogo,
    entitiesProduct,
    entitiesGallery,
    entitiesGridColumns,
  } = useProductTileEntities();

  if (showFromProductTileEntities && entitiesGallery.elements.length) {
    return (
      <StrictMode>
        <StyledSplitViewGrid
          split={'6:5'}
          contentStart={
            <>
              <ImageSectionWrapper>
                <ProductImageGridView
                  gridColumns={entitiesGridColumns}
                  badges={[]}
                  brand={entitiesBrandLogo.brand}
                  gallery={entitiesGallery}
                  product={entitiesProduct}
                  onImageClick={() => {}}
                />
              </ImageSectionWrapper>
            </>
          }
          contentEnd={
            <div>
              <BuyboxTopSpacer>
                <StyledBreadcrumbsLoading />
                <PriceAndBrandLoading />
                <StyledProductSelectionGridSkeleton />
                <StyledAddToBasketSectionSkeleton />
                <ProductUSPsLoading />
                <ProductDetailsAccordionsLoading />
              </BuyboxTopSpacer>
            </div>
          }
        />
      </StrictMode>
    );
  }

  return (
    <FullArticleDetail
      selectedSize={selectedSize}
      setSelectedSize={setSelectedSize}
    />
  );
};

const FullArticleDetail: React.FC<{
  selectedSize: SelectedSize;
  setSelectedSize: SetSelectedSize;
}> = ({selectedSize, setSelectedSize}) => {
  const {open: openProductMiniADPDialog} = useMiniAdpDialogOpenedState();
  const {open: openBasketFlyout} = useBasketFlyoutOpenedState();
  useIsFeatureActive('ab_adp_new_tab', {abTest: true});
  const activateNewBannerFramework = useIsNewBannerFrameworkActive();

  const addToBasketRef = useRef<HTMLElement | null>(null);
  const [recoType, setRecoType] = useState<string>();

  const productImageState = useProductImageDetailsModalState();
  const splitViewRef = useRef<HTMLElement | null>(null);

  const [imageGridInactive, setImageGridInactive] = useState(false);
  const [previewImage, setPreviewImage] = useState<ImageType>();
  const [productInfo, setProductInfo] = useState<{
    productName: string;
    productBrandName: string;
  }>();
  const [addToBasketTrackingData, setAddToBasketTrackingData] =
    useState<AddToBasketTrackingData>();
  const productId = useProductIdFromSlug();

  const [freeDeliverySection, setFreeDeliverySection] =
    useState<FreeDeliverySection>();

  const [statusSuccessSections, setStatusSuccessSections] = useState<
    AddItemResponseStatus_Success_Section[]
  >([]);

  const isRTL = useIsRTL();

  const isCouponsEnabled = useIsCouponsEnabled();

  const preloadOutfitDetailPage = () => {
    OutfitDetailPage.preload();
  };

  const openMiniADP = useCallback(
    ({
      productId: clickedProductId,
      locationId,
      couponPromotionId,
    }: {
      productId: number;
      locationId?: string;
      couponPromotionId?: string;
    }) =>
      openProductMiniADPDialog({
        productId: clickedProductId,
        locationId,
        couponPromotionId,
      }),
    [openProductMiniADPDialog],
  );

  return (
    <>
      <AddToWishlistRedirect productId={productId!} />

      <StrictMode>
        <StyledSplitViewGrid
          ref={splitViewRef}
          split={'6:5'}
          contentStart={
            <Suspense>
              <MemoPageMetaData selectedSize={selectedSize} />
              <MemoPageMetaDataSectionJsonLd />
              <StyledSticky
                data-testid="stickyImageGrid"
                overflowScroll="end"
                experimentalNative
                container={splitViewRef}
                // Placing the key on the Sticky component ensures that it remounts when necessary.
                // This is a workaround for a bug in the Sticky library that affects scroll restoration when navigating back in history.
                // To track the progress on resolving this issue, refer to: https://github.com/garthenweb/react-stickup/issues/47
                // Remounting the component may have a performance impact, but it provides a temporary solution until the library issue is resolved.
                key={productId}
                defaultOffsetTop={DESKTOP_STICKY_HEIGHT}
              >
                <ImageSectionWrapper>
                  <>
                    <BackButton
                      Icon={CloseButtonIcon}
                      isRTL={isRTL}
                      addFirstProductId
                    />
                    <MemoProductImageGrid
                      inactive={imageGridInactive}
                      onImageClick={productImageState.open}
                      key={productId}
                      previewImage={previewImage}
                    />
                  </>
                </ImageSectionWrapper>
              </StyledSticky>
            </Suspense>
          }
          contentEnd={
            <Suspense>
              <StyledSticky
                data-testid="stickyProductDetails"
                overflowScroll="end"
                experimentalNative
                container={splitViewRef}
                // Placing the key on the Sticky component ensures that it remounts when necessary.
                // This is a workaround for a bug in the Sticky library that affects scroll restoration when navigating back in history.
                // To track the progress on resolving this issue, refer to: https://github.com/garthenweb/react-stickup/issues/47
                // Remounting the component may have a performance impact, but it provides a temporary solution until the library issue is resolved.
                key={productId}
                defaultOffsetTop={DESKTOP_STICKY_HEIGHT}
              >
                <BuyboxTopSpacer>
                  <div>
                    <StyledBreadcrumbs />
                    <MemoPriceAndBrand
                      brandLogoAlignment="left"
                      selectedSize={selectedSize}
                      shouldDisplayReviewsSection
                      showActiveCouponBadge
                      setProductInfo={setProductInfo}
                      PriceLegalInfoLayoutComponent={ModalDialog}
                    />
                    {isCouponsEnabled && <CouponHint productId={productId!} />}
                  </div>
                  <MemoProductSelection
                    ColorSelectionLayoutComponent={ProductSelectionDropdown}
                    showProductSelectionAsGrid
                    setImageGridInactive={setImageGridInactive}
                    SoldOutComponent={SoldOutState}
                    setPreviewImage={setPreviewImage}
                  />
                  <div>
                    <MemoAddToBasketSection
                      ref={addToBasketRef}
                      selectedSize={selectedSize}
                      setSelectedSize={setSelectedSize}
                      SizeSelectionLayoutComponent={SizeSelectionDropdown}
                      showPriceInSizeSelectionHeader={false}
                      SizeReminderLayoutComponent={ModalDialog}
                      showWishlistButton
                      shouldUseLightBorder
                      sizeSelectionStateManagerKey={
                        SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
                      }
                      shouldUseSquareWishButton
                      shouldDisplaySizeAdvisorAboveSizeSelection
                      shouldOptimiseElementsSpacing
                      onAddToBasketSuccess={({
                        freeDeliverySection,
                        statusSuccessSections,
                      }) => {
                        openBasketFlyout(productId!);
                        setFreeDeliverySection(freeDeliverySection);
                        setStatusSuccessSections(statusSuccessSections);
                      }}
                      SoldOutSizeReminderComponent={MemoSoldOutSizeReminderForm}
                      renderSoldoutSelectionAsDropdown
                    />
                    <MemoHotProductSection />
                    {selectedSize && (
                      <MerchantInfoWrapper>
                        <MerchantInfoWithLegal
                          availability={selectedSize?.availability}
                          showBorderRadius
                          productId={productId!}
                          sizeId={selectedSize.sizeId}
                        />
                      </MerchantInfoWrapper>
                    )}
                  </div>
                  <div>
                    {activateNewBannerFramework ? (
                      <PromotionBannerSlot>
                        <PromotionBanner
                          ContentPopperLayoutComponent={ModalDialog}
                        />
                      </PromotionBannerSlot>
                    ) : (
                      <BASPlacement />
                    )}
                    <MemoProductUSPs
                      selectedSize={selectedSize}
                      InfoLayoutComponent={ModalDialog}
                    />
                  </div>
                  <div>
                    <MemoProductDetailsAccordions
                      selectedSize={selectedSize}
                      SizeChartLayout={ModalDialog}
                      SizeSystemLayout={Dropdown}
                    />
                    <StyledLegalReport
                      ContentPopperLayoutComponent={ModalDialog}
                    />
                  </div>
                  <div>
                    <CoinsBanner ContentPopperLayoutComponent={Drawer} />
                    <StyledMemoMerchantBanner />
                  </div>
                </BuyboxTopSpacer>
              </StyledSticky>
            </Suspense>
          }
        />
        <CleanActiveCoupon productId={productId!} />
        <MemoProductImageDetailsModal {...productImageState} />
        <SoldOutSimilarProductsReco />
        <MemoProductInformationBox
          onAddToBasketCtaClick={openMiniADP}
          onAddToBasketSuccess={() => {
            openBasketFlyout(productId!);
          }}
          productTileConfig={{
            gridColumns: 4,
            onColorClick: colorProductId => {
              openProductMiniADPDialog({productId: colorProductId});
            },
          }}
        />

        <StyledRecommendations
          onAddToBasketCtaClick={openMiniADP}
          ProductTileComponent={InteractiveProductTile}
          showInlineOutfitSlider={true}
          productInfo={productInfo}
          setRecoType={setRecoType}
          preloadOutfitDetailPage={preloadOutfitDetailPage}
          setAddToBasketTrackingData={setAddToBasketTrackingData}
        />
        <LastSeenProductIdsUpdater />

        {isCouponsEnabled && (
          <MemoProminentBannerSection
            couponVariant="coupon-sticky-bar"
            selectedSize={selectedSize}
            hideCampaignCountdown
          />
        )}
      </StrictMode>

      <ProductMiniAdpWithBasketFlyout
        showAddToWishlistButton
        sizeSelectionStateManagerKey={
          SizeSelectionOpenedStateKey.BASKET_FLYOUT_SIZE_SELECTION
        }
        freeDeliverySection={freeDeliverySection}
        statusSuccessSections={statusSuccessSections}
        recoType={recoType}
        addToBasketTrackingData={addToBasketTrackingData}
      />
    </>
  );
};

const CloseButtonIcon: React.FC = () => (
  <Icon asset={GenericIconName.ArrowLeft} size="S" />
);

const ImageSectionWrapper = styled.section`
  position: relative;
  padding-top: 24px;
  ${gridCols(6)}
`;

const SizeSelectionDropdown: React.FC<DropDownProps> = props => {
  const {isOpen: isSizeRecommendationInfoOpen} = useSizeRecommendationInfo();

  return (
    <StyledDropdown
      {...props}
      dropdownBodyAlign={DropdownBodyAlignment.LEFT}
      animationDuration={10}
      dropdownBodyWidth="350px"
      disableClickOutside={isSizeRecommendationInfoOpen}
      isActive
    />
  );
};

const ProductSelectionDropdown: React.FC<DropDownProps> = props => {
  return (
    <StyledDropdown
      {...props}
      dropdownBodyAlign={DropdownBodyAlignment.LEFT}
      animationDuration={10}
      dropdownBodyWidth="350px"
      closeOnContentClick={true}
      isActive
    />
  );
};

const StyledDropdown = styled(DropdownBase)`
  &&& {
    display: flow-root;
  }

  > button {
    border: solid 1px ${COLORS.Grey3};
  }

  & ${DropdownBody}, & ul {
    width: 100%;
    max-width: 100%;
  }
`;

// Used to set a space on the top of the buy box and don't
// mess with the alignment of the sticky component
const BuyboxTopSpacer = styled.div`
  display: flex;
  gap: 32px;
  flex-direction: column;
`;

const breadcrumbsStyles = `
  margin: 24px 0 0;
`;

const StyledBreadcrumbs = styled(MemoBreadcrumbs)`
  ${breadcrumbsStyles}
`;

const StyledBreadcrumbsLoading = styled(BreadcrumbsADPLoading)`
  ${breadcrumbsStyles}
`;

const BackButton = styled(GoBack)<{
  isRTL: boolean;
  $isInsideBreadcrumbs?: boolean;
}>`
  fill: ${COLORS.Grey1};
  position: absolute;
  left: ${props => (props.$isInsideBreadcrumbs ? '16px' : 0)};
  top: ${props => (props.$isInsideBreadcrumbs ? '20px' : '24px')};
  width: ${props => (props.$isInsideBreadcrumbs ? '32px' : 'unset')};
  height: ${props => (props.$isInsideBreadcrumbs ? '32px' : 'unset')};
  margin: ${props =>
    props.$isInsideBreadcrumbs
      ? '-17px'
      : 'unset'}; // cancel out the padding on the button

  transition: fill 0.3s ease;
  z-index: 1;

  transform: ${({isRTL}) => (isRTL ? 'scaleX(-1)' : 'unset')};

  @media (pointer: fine) {
    &:hover {
      fill: ${COLORS.Black};
    }
  }
`;

const StyledProductSelectionGridSkeleton = styled(
  ProductSelectionGridSkeleton,
)``;

const addToBasketSectionStyles = `
  margin: 1.5rem 0 1.35rem 0;
`;

const StyledAddToBasketSectionSkeleton = styled(AddToBasketSectionSkeleton)`
  ${addToBasketSectionStyles}
`;

const StyledSplitViewGrid = styled(SplitViewGrid)`
  margin-bottom: 12vh;
`;

const StyledLegalReport = styled(LegalReport)`
  padding: 16px 0;
  margin-top: 0.5rem;
`;

const StyledMemoMerchantBanner = styled(MemoMerchantBanner)`
  padding-top: 15px;
`;

const StyledSticky = styled(Sticky)<
  {
    $maxWidth?: number;
  } & {[key: string]: any}
>`
  max-width: ${({$maxWidth}) => ($maxWidth ? `${$maxWidth}px` : 'unset')};
  padding-bottom: 60px;
  z-index: ${Z_INDEXES.ADP_BUYBOX};
`;

const StyledRecommendations = styled(MemoRecommendations)`
  ${RecoHeaderWrapperLinaria} {
    ${tabletAndDownLinaria('padding-left: 15px;')}
  }
`;

const MerchantInfoWrapper = styled.div`
  margin-top: 1.35rem;
`;

const PromotionBannerSlot = styled.div`
  margin-bottom: 30px;

  &:empty {
    margin-bottom: 0;
  }
`;
